import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  type GridRenderEditCellParams,
  useGridApiContext,
} from '@mui/x-data-grid';

import PreviewDialog from './PreviewDialog';
import { useConfigSectionEditor } from './useConfigSectionEditor';
import { usePendingGridEdits } from './usePendingGridEdits';
import {
  SaveSuccessSnackbar,
  SectionAlerts,
  SectionProvenance,
  SectionTitle,
  UnsavedChangesGuard,
} from './SectionEditorChrome';

interface PlatformRow {
  id: number;
  clientName: string;
  platforms: string[];
}

const PLATFORM_OPTIONS = ['huntress', 'threatlocker', 'msp360'] as const;

function rowsToRecord(rows: PlatformRow[]): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  for (const row of rows) {
    const key = row.clientName.trim();
    if (key) {
      result[key] = row.platforms;
    }
  }
  return result;
}

function recordToRows(required: Record<string, string[]>): PlatformRow[] {
  return Object.entries(required).map(([client, platforms], i) => ({
    id: i,
    clientName: client,
    platforms,
  }));
}

/** Two rows for the same client would silently collapse into one on save. */
function validatePlatformRows(rows: PlatformRow[]): string | null {
  const seen = new Set<string>();
  for (const row of rows) {
    const client = row.clientName.trim();
    if (!client) continue;
    if (seen.has(client)) return `"${client}" is listed more than once.`;
    seen.add(client);
  }
  return null;
}

/**
 * Custom multi-select edit cell for the platforms column.
 * Renders a MUI Select with multiple selection for the three platform options.
 */
function PlatformsEditCell(params: GridRenderEditCellParams<PlatformRow, string[]>) {
  const { id, field, value } = params;
  const apiRef = useGridApiContext();

  function handleChange(newValue: string[]) {
    void apiRef.current.setEditCellValue({ id, field, value: newValue });
  }

  return (
    <Select
      multiple
      value={value ?? []}
      onChange={(e) => handleChange(e.target.value as string[])}
      size="small"
      fullWidth
      renderValue={(selected: string[]) => selected.join(', ')}
      sx={{ fontSize: '0.875rem' }}
    >
      {PLATFORM_OPTIONS.map((p) => (
        <MenuItem key={p} value={p}>
          {p}
        </MenuItem>
      ))}
    </Select>
  );
}

/**
 * Platform Requirements settings section.
 *
 * Editable DataGrid mapping client names to their required platform set.
 * Uses a custom multi-select edit cell for the platforms column.
 * Preview-before-apply via PreviewDialog.
 */
export default function PlatformRequirementsSection() {
  const editor = useConfigSectionEditor<PlatformRow, Record<string, string[]>>({
    section: 'required-platforms',
    label: 'Platform Requirements',
    toRows: recordToRows,
    fromRows: rowsToRecord,
    validateRows: validatePlatformRows,
  });
  const { rows, setRows } = editor;
  const pendingEdits = usePendingGridEdits<PlatformRow>();

  function handleAddRow() {
    const newId = rows.length > 0 ? Math.max(...rows.map((r) => r.id)) + 1 : 0;
    setRows((prev) => [...prev, { id: newId, clientName: '', platforms: ['huntress'] }]);
  }

  function handleDeleteRow(id: number) {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  function handleProcessRowUpdate(newRow: PlatformRow): PlatformRow {
    setRows((prev) => prev.map((r) => (r.id === newRow.id ? newRow : r)));
    return newRow;
  }

  const columns: GridColDef<PlatformRow>[] = [
    {
      field: 'clientName',
      headerName: 'Client Name',
      flex: 1,
      editable: true,
    },
    {
      field: 'platforms',
      headerName: 'Required Platforms',
      flex: 1.5,
      editable: true,
      renderEditCell: (params: GridRenderEditCellParams<PlatformRow, string[]>) => (
        <PlatformsEditCell {...params} />
      ),
      renderCell: (params: GridRenderCellParams<PlatformRow, string[]>) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
          {(params.value ?? []).map((p) => (
            <Chip key={p} label={p} size="small" />
          ))}
        </Box>
      ),
    },
    {
      field: '_actions',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<PlatformRow>) => (
        <IconButton
          size="small"
          onClick={() => handleDeleteRow(params.row.id)}
          aria-label="Delete row"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  if (editor.isLoading && !editor.data) {
    return (
      <Box sx={{ py: 2 }}>
        <Skeleton variant="rounded" height={160} sx={{ mb: 2 }} />
        <Skeleton variant="text" width={140} />
      </Box>
    );
  }

  return (
    <Box>
      <SectionTitle title="Platform Requirements" isDirty={editor.isDirty} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Defines which platforms are required for each client whose devices are managed by Addigy (no
        Automate EDF flags). A device missing a required platform is never shown as Healthy.
      </Typography>

      <SectionAlerts
        loadError={editor.loadError}
        hasConflict={editor.hasConflict}
        validationError={editor.validationError}
        onDiscard={editor.discardChanges}
      />

      <DataGrid
        rows={rows}
        columns={columns}
        processRowUpdate={handleProcessRowUpdate}
        {...pendingEdits.gridProps}
        disableRowSelectionOnClick
        hideFooter={rows.length <= 100}
        autoHeight
        rowHeight={48}
        sx={{ mb: 1.5 }}
      />

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={handleAddRow} variant="outlined">
          Add Client
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => editor.openPreview(pendingEdits.withPendingEdits(rows))}
          disabled={
            editor.preview.isPending ||
            (!editor.isDirty && !pendingEdits.editing) ||
            !!editor.validationError
          }
        >
          {editor.preview.isPending ? 'Computing...' : 'Preview Changes'}
        </Button>
        {editor.isDirty && (
          <Button size="small" color="inherit" onClick={editor.discardChanges}>
            Discard
          </Button>
        )}
      </Box>

      <SectionProvenance record={editor.record} />

      <PreviewDialog
        open={editor.preview.open}
        preview={editor.preview.data}
        previewLoading={editor.preview.isPending}
        error={editor.preview.error}
        applying={editor.applying}
        applyError={editor.applyError}
        writeDisabledReason={editor.writeDisabledReason}
        onApply={() => void editor.apply()}
        onCancel={editor.closePreview}
      />
      <SaveSuccessSnackbar message={editor.successMessage} onClose={editor.clearSuccess} />
      <UnsavedChangesGuard when={editor.isDirty} />
    </Box>
  );
}
